import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { CustomersIndexData } from '@entities/customer'
import type { ApiResponse } from '@shared/api'

import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useCustomersStore } from '../../model/list/store'

export const CUSTOMERS_LOAD_ERROR = 'Failed to load customers. Please reload the page.'

interface CustomersApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/customers` — the index. Returns `{ customers, meta }` with simple pagination (no total).
 * The request is rebuilt from the store on every call: `meta.next_page_url` renames the search to `q`
 * and drops the tutor filters, so it must never be followed.
 */
export const useCustomersApi = async (options?: CustomersApiOptions): Promise<CustomersIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useCustomersStore()

  const url = useAdminRoutes('admin.customers.data', pick({
    query: store.getFilterSearch,
    status: store.getFilterStatus,
    stage: store.getFilterStages.length ? store.getFilterStages : undefined,
    from: store.getFilterFrom,
    to: store.getFilterTo,
    source_funnel: store.getFilterSourceFunnel,
    tutor_id: store.getFilterTutorId,
    studying_status: store.getFilterStudyingStatus,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(CUSTOMERS_LOAD_ERROR)
      return null
    }

    const indexData: CustomersIndexData | null = data.data || null

    if (indexData) {
      store.setCustomers(indexData.customers)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch {
    toast.error(CUSTOMERS_LOAD_ERROR)
    return null
  } finally {
    store.setLoading(false)
  }
}
